/* eslint-disable react/require-default-props */
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { Popup as KendoPopup } from '@progress/kendo-react-popup';
import './PopupStyles.scss';
import { PopupSection } from './PopupSection';
import { PopupTitle } from './PopupTitle';
import { PopupSplit } from './PopupSplit';
import { PopupSearchInput } from './PopupSearchInput';
import { PopupActionList } from './PopupActionList';
import { useCloseOnEscape } from './useCloseOnEscape';
/** Prefer strict placement over Kendo’s default vertical flip (flip breaks `top-*` in tight viewports). */
const DEFAULT_COLLISION = { horizontal: 'fit', vertical: 'none' };
const VIEWPORT_PADDING = 8;
/**
 * Popup Component
 * Wraps Kendo Popup with ClickAwayListener for consistent popup behavior
 * @version 1.0.0
 * @since 2026-01-27
 */
export function Popup({ anchor, show, onClose, children, containerClass, containerStyle, position = 'bottom-center', collision = DEFAULT_COLLISION }) {
    // Map position to Kendo Popup alignment
    const getAnchorAlign = () => {
        switch (position) {
            // Top positions
            case 'top-left':
                return { horizontal: 'left', vertical: 'top' };
            case 'top-center':
                return { horizontal: 'center', vertical: 'top' };
            case 'top-right':
                return { horizontal: 'right', vertical: 'top' };
            // Bottom positions
            case 'bottom-left':
                return { horizontal: 'left', vertical: 'bottom' };
            case 'bottom-center':
                return { horizontal: 'center', vertical: 'bottom' };
            case 'bottom-right':
                return { horizontal: 'right', vertical: 'bottom' };
            // Left positions
            case 'left-top':
                return { horizontal: 'left', vertical: 'top' };
            case 'left-center':
                return { horizontal: 'left', vertical: 'center' };
            case 'left-bottom':
                return { horizontal: 'left', vertical: 'bottom' };
            // Right positions
            case 'right-top':
                return { horizontal: 'right', vertical: 'top' };
            case 'right-center':
                return { horizontal: 'right', vertical: 'center' };
            case 'right-bottom':
                return { horizontal: 'right', vertical: 'bottom' };
            default:
                return { horizontal: 'center', vertical: 'bottom' };
        }
    };
    const getPopupAlign = () => {
        switch (position) {
            // Top positions — popup above anchor (popup bottom edge meets anchor top)
            case 'top-left':
                return { horizontal: 'left', vertical: 'bottom' };
            case 'top-center':
                return { horizontal: 'center', vertical: 'bottom' };
            case 'top-right':
                return { horizontal: 'right', vertical: 'bottom' };
            // Bottom positions — popup below anchor (popup top edge meets anchor bottom)
            case 'bottom-left':
                return { horizontal: 'left', vertical: 'top' };
            case 'bottom-center':
                return { horizontal: 'center', vertical: 'top' };
            case 'bottom-right':
                return { horizontal: 'right', vertical: 'top' };
            // Left positions - popup appears to the left of anchor (popup's right edge aligns to anchor's left edge)
            case 'left-top':
                return { horizontal: 'right', vertical: 'top' };
            case 'left-center':
                return { horizontal: 'right', vertical: 'center' };
            case 'left-bottom':
                return { horizontal: 'right', vertical: 'bottom' };
            // Right positions - popup appears to the right of anchor (popup's left edge aligns to anchor's right edge)
            case 'right-top':
                return { horizontal: 'left', vertical: 'top' };
            case 'right-center':
                return { horizontal: 'left', vertical: 'center' };
            case 'right-bottom':
                return { horizontal: 'left', vertical: 'bottom' };
            default:
                return { horizontal: 'center', vertical: 'top' };
        }
    };
    const anchorAlign = getAnchorAlign();
    const popupAlign = getPopupAlign();
    const containerRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(null);
    useLayoutEffect(() => {
        if (!show || !anchor) {
            setMaxHeight(null);
            return;
        }
        const getPreferredVerticalPlacement = () => {
            if (position.startsWith('top-'))
                return 'above';
            if (position.startsWith('bottom-'))
                return 'below';
            if (position.endsWith('-top'))
                return 'above';
            if (position.endsWith('-bottom'))
                return 'below';
            return 'center';
        };
        // Capture the natural content height once, before flex layout (maxHeight) shrinks
        // containerRef.scrollHeight down to maxHeight. All subsequent calls reuse this
        // snapshot so the scroll-event listener does not incorrectly clear the constraint.
        let naturalContentHeight = null;
        const recalculateMaxHeight = () => {
            if (!containerRef.current)
                return;
            // On the first call maxHeight is still null and the layout is unshrunk.
            // On later calls (resize, scroll) we reuse the stored snapshot.
            if (naturalContentHeight === null || containerRef.current.scrollHeight > naturalContentHeight) {
                naturalContentHeight = containerRef.current.scrollHeight;
            }
            const contentHeight = naturalContentHeight;
            const anchorRect = anchor.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const availableAbove = Math.max(0, anchorRect.top - VIEWPORT_PADDING);
            const availableBelow = Math.max(0, viewportHeight - anchorRect.bottom - VIEWPORT_PADDING);
            const preferredPlacement = getPreferredVerticalPlacement();
            const canFlipVertically = collision.vertical === 'flip';
            let preferredAvailableHeight = Math.max(availableAbove, availableBelow);
            let alternateAvailableHeight = preferredAvailableHeight;
            if (preferredPlacement === 'above') {
                preferredAvailableHeight = availableAbove;
                alternateAvailableHeight = availableBelow;
            }
            else if (preferredPlacement === 'below') {
                preferredAvailableHeight = availableBelow;
                alternateAvailableHeight = availableAbove;
            }
            if (contentHeight <= preferredAvailableHeight) {
                setMaxHeight(null);
                return;
            }
            if (canFlipVertically && contentHeight <= alternateAvailableHeight) {
                setMaxHeight(null);
                return;
            }
            const bestAvailableHeight = canFlipVertically
                ? Math.max(preferredAvailableHeight, alternateAvailableHeight)
                : preferredAvailableHeight;
            const viewportSafeHeight = Math.max(0, viewportHeight - VIEWPORT_PADDING * 2);
            const constrainedHeight = Math.min(bestAvailableHeight, viewportSafeHeight);
            if (constrainedHeight <= 0) {
                setMaxHeight(viewportSafeHeight > 0 ? viewportSafeHeight : null);
                return;
            }
            setMaxHeight(constrainedHeight);
        };
        recalculateMaxHeight();
        window.addEventListener('resize', recalculateMaxHeight);
        window.addEventListener('scroll', recalculateMaxHeight, true);
        // Re-measure when the popup body itself changes size (e.g. a collapsible
        // section expands after open). The naturalContentHeight guard inside
        // recalculateMaxHeight prevents the observer from clearing a constraint
        // that was already correctly applied.
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recalculateMaxHeight) : null;
        if (containerRef.current && resizeObserver)
            resizeObserver.observe(containerRef.current);
        return () => {
            window.removeEventListener('resize', recalculateMaxHeight);
            window.removeEventListener('scroll', recalculateMaxHeight, true);
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
        };
    }, [anchor, collision.vertical, position, show]);
    useCloseOnEscape(show, onClose);
    const mergedContainerStyle = useMemo(() => {
        if (maxHeight == null)
            return containerStyle !== null && containerStyle !== void 0 ? containerStyle : {};
        // Use flex column layout so popup__section--y-fill can own its scroll region.
        // Do NOT set overflow on the container: flex guarantees sections fill max-height
        // exactly, so there is nothing to clip. Setting overflow:hidden would create a
        // scroll container in Chrome that absorbs wheel events before they reach the
        // scrollable section, breaking mouse-wheel scrolling over list items.
        return Object.assign(Object.assign({ display: 'flex', flexDirection: 'column' }, containerStyle), { maxHeight });
    }, [containerStyle, maxHeight]);
    return (React.createElement(KendoPopup, { className: "qmb-ui-popup", animate: false, style: { zIndex: 10001 }, anchor: anchor, show: show, onClose: onClose, anchorAlign: anchorAlign, popupAlign: popupAlign, collision: collision },
        React.createElement(ClickAwayListener, { onClickAway: onClose },
            React.createElement("div", { ref: containerRef, className: containerClass, style: mergedContainerStyle }, children))));
}
// Compound component API — sub-components namespaced under Popup
Popup.Section = PopupSection;
Popup.Title = PopupTitle;
Popup.Split = PopupSplit;
Popup.SearchInput = PopupSearchInput;
Popup.ActionList = PopupActionList;
